/**
 * Mutable working copy of a GameState used while applying a single action.
 * The input state is never mutated; `finish()` produces the next immutable state.
 */
import { mustParse } from './geometry.ts';
import { indexMap } from './map.ts';
import type { Block, Reason } from './reasons.ts';
import { Rng } from './rng.ts';
import type { FortState, GameEvent, GameState, GameStatus, ShipState, Side, TurnState } from './types.ts';
import { otherSide, shipsAt, type World } from './world.ts';

type Mutable<T> = { -readonly [K in keyof T]: T[K] };

export class RuleViolation extends Error {
  readonly rule: string;
  readonly reason: Reason;

  constructor(rule: string, reason: Reason, message: string) {
    super(`[${rule}] ${message}`);
    this.rule = rule;
    this.reason = reason;
    this.name = 'RuleViolation';
  }
}

export function check(blocked: Block | null, message: string): void {
  if (blocked !== null) throw new RuleViolation(blocked.rule, blocked.reason, message);
}

export class Draft implements World {
  readonly map: GameState['map'];
  readonly ruleset: GameState['ruleset'];
  ships: Mutable<ShipState>[];
  forts: Mutable<FortState>[];
  turn: Mutable<TurnState>;
  holdCount: Record<Side, number>;
  drawOffer: GameState['drawOffer'];
  status: GameStatus;
  readonly rng: Rng;
  readonly events: GameEvent[] = [];

  private readonly base: GameState;

  constructor(base: GameState) {
    this.base = base;
    this.map = base.map;
    this.ruleset = base.ruleset;
    this.ships = base.ships.map((s) => ({ ...s }));
    this.forts = base.forts.map((f) => ({ ...f }));
    this.turn = { ...base.turn };
    this.holdCount = { ...base.holdCount };
    this.drawOffer = base.drawOffer;
    this.status = base.status;
    this.rng = new Rng(base.rng);
  }

  emit(event: GameEvent): void {
    this.events.push(event);
  }

  ship(id: string): Mutable<ShipState> {
    const ship = this.ships.find((s) => s.id === id);
    if (!ship) throw new RuleViolation('R-TURN-02', 'unknownUnit', `Unknown ship '${id}'`);
    return ship;
  }

  fort(id: string): Mutable<FortState> {
    const fort = this.forts.find((f) => f.id === id);
    if (!fort) throw new RuleViolation('R-TURN-02', 'unknownUnit', `Unknown fort '${id}'`);
    return fort;
  }

  get playing(): boolean {
    return this.status.kind === 'playing';
  }

  end(status: GameStatus): void {
    if (!this.playing) return;
    this.status = status;
    this.emit({ type: 'gameEnded', status });
  }

  // -------------------------------------------------------------------------
  // Damage, sinking, destruction
  // -------------------------------------------------------------------------

  /** R-FIRE-08 / R-STORM-03 / R-SREP-04 */
  sink(ship: Mutable<ShipState>, cause: 'fire' | 'fort_destroyed' | 'wreck'): void {
    if (ship.sunk) return;
    ship.sunk = true;
    ship.dockedOnTurn = null;
    if (ship.repairingFort !== null) {
      this.cancelFortRepair(this.fort(ship.repairingFort), 'crew_lost');
    }
    if (cause === 'wreck') this.emit({ type: 'shipWrecked', shipId: ship.id, cell: ship.cell });
    else this.emit({ type: 'shipSunk', shipId: ship.id, cause });
    this.checkAnnihilation();
  }

  /** R-END-02 */
  checkAnnihilation(): void {
    for (const side of ['red', 'blue'] as const) {
      if (!this.ships.some((s) => s.side === side && !s.sunk)) {
        this.end({ kind: 'won', winner: otherSide(side), reason: 'annihilation' });
        return;
      }
    }
  }

  damageShip(ship: Mutable<ShipState>, amount: number): void {
    ship.damage += amount;
    this.emit({ type: 'shipDamaged', shipId: ship.id, damage: ship.damage });
    if (ship.damage >= this.ruleset.shipClasses[ship.cls].sinksAt) this.sink(ship, 'fire');
  }

  hitFort(fort: Mutable<FortState>, amount: number): void {
    fort.hits += amount;
    this.emit({ type: 'fortHit', fortId: fort.id, hits: fort.hits });
    if (fort.hits >= this.ruleset.fortClasses[fort.cls].destroyedAt) this.destroyFort(fort);
  }

  /** R-FORT-01, R-FORT-03 */
  destroyFort(fort: Mutable<FortState>): void {
    fort.destroyed = true;
    fort.shotsLeft = 0;
    if (fort.repair) this.cancelFortRepair(fort, 'fort_destroyed');
    this.emit({ type: 'fortDestroyed', fortId: fort.id });
    for (const ship of this.ships) {
      if (!ship.sunk && ship.dockedOnTurn !== null && fort.portCells.includes(ship.cell)) {
        this.sink(ship, 'fort_destroyed');
      }
    }
  }

  cancelFortRepair(
    fort: Mutable<FortState>,
    reason: 'voluntary' | 'crew_lost' | 'fort_destroyed' | 'completed',
  ): void {
    if (!fort.repair) return;
    for (const id of fort.repair.crew) {
      const crew = this.ships.find((s) => s.id === id);
      if (crew) crew.repairingFort = null;
    }
    fort.repair = null;
    this.emit({ type: 'fortRepairStopped', fortId: fort.id, reason });
  }

  // -------------------------------------------------------------------------
  // Turn start
  // -------------------------------------------------------------------------

  /** Starts turn `number` for `side`, resetting per-turn flags (R-FIRE-04, R-TURN-06). */
  startTurn(number: number, side: Side): void {
    for (const ship of this.ships) {
      ship.moved = false;
      ship.inGroup = false;
      ship.fired = false;
      ship.firedInPort = false;
      ship.shotsLeft = ship.side === side && !ship.sunk ? this.ruleset.shipClasses[ship.cls].shotsPerTurn : 0;
    }
    for (const fort of this.forts) {
      fort.fired = false;
      fort.shotsLeft =
        fort.side === side && !fort.destroyed ? this.ruleset.fortClasses[fort.cls].shotsPerTurn : 0;
    }
    const royal = indexMap(this.map).royalCell(otherSide(side));
    this.turn = {
      number,
      side,
      phase: 'open',
      group: [],
      spin: null,
      royalAtStart: royal
        ? shipsAt(this, royal)
            .filter((s) => s.side === side)
            .map((s) => s.id)
        : [],
    };
    this.emit({ type: 'turnStarted', turn: number, side });
  }

  isActivePortCell(cell: string): boolean {
    const fortId = indexMap(this.map).portOf(mustParse(cell));
    return fortId !== null && !this.fort(fortId).destroyed;
  }

  finish(): GameState {
    return {
      ...this.base,
      rng: this.rng.state,
      ships: this.ships,
      forts: this.forts,
      turn: this.turn,
      holdCount: this.holdCount,
      drawOffer: this.drawOffer,
      status: this.status,
      actionCount: this.base.actionCount + 1,
    };
  }
}
